import React from 'react';
import './BookingBottomBar.css';
import { BOOKING_STEPS, LandingMode } from '../../constants/bookingSteps';
import {
    canShowBackButton,
    canShowNextButton,
    getNextButtonLabel,
    shouldShowBookingBottomBar,
} from '../../utils/bookingNavigation';

interface BookingBottomBarProps {
    currentStep: number;
    visitedSteps: Set<number>;
    landingMode: LandingMode;
    hasMultipleRooms: boolean;
    showCalendar: boolean;
    hasSelection: boolean;
    isPaymentReady: boolean;
    isSummaryOpen: boolean;
    includeSummaryStep?: boolean;
    totalAmount?: number | null;
    onSummaryOpenChange: (open: boolean) => void;
    onBack: () => void;
    onNext: () => void;
    onPay: () => void;
}

const currencyFormatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const LockIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
);

const BookingBottomBar: React.FC<BookingBottomBarProps> = ({
    currentStep,
    visitedSteps,
    landingMode,
    hasMultipleRooms,
    showCalendar,
    hasSelection,
    isPaymentReady,
    isSummaryOpen,
    includeSummaryStep = true,
    totalAmount = null,
    onSummaryOpenChange,
    onBack,
    onNext,
    onPay,
}) => {
    const showBar = shouldShowBookingBottomBar(
        currentStep,
        hasSelection,
        hasMultipleRooms,
        landingMode,
        showCalendar,
        visitedSteps,
        includeSummaryStep
    );
    const showBack = canShowBackButton(
        currentStep,
        hasMultipleRooms,
        landingMode,
        showCalendar,
        includeSummaryStep
    );
    const showNext = canShowNextButton(currentStep, visitedSteps);
    const showPaymentButton = currentStep === BOOKING_STEPS.PERSONAL_DATA;
    const isSummaryStep = currentStep === BOOKING_STEPS.SUMMARY;
    const summaryLocked =
        currentStep === BOOKING_STEPS.PERSONAL_DATA || isSummaryStep;
    const showSummary = hasSelection && currentStep >= BOOKING_STEPS.TURN && !summaryLocked;
    const showTotal = isSummaryStep && totalAmount !== null;

    if (!showBar) {
        return null;
    }

    const handleBack = () => {
        if (!summaryLocked) {
            onSummaryOpenChange(false);
        }
        onBack();
    };

    const handleNext = () => {
        onSummaryOpenChange(false);
        onNext();
    };

    return (
        <nav
            className={`booking-bottom-bar${isSummaryStep ? ' booking-bottom-bar--summary' : ''}`}
            aria-label="Navegación de reserva"
        >
            <div className="booking-bottom-bar-inner">
                {showBack ? (
                    <button
                        type="button"
                        className="booking-bottom-btn booking-bottom-btn--back"
                        onClick={handleBack}
                        aria-label="Paso anterior"
                    >
                        <span className="booking-bottom-btn-icon" aria-hidden="true">‹</span>
                        <span className="booking-bottom-btn-label">Atrás</span>
                    </button>
                ) : (
                    <span className="booking-bottom-spacer" aria-hidden="true" />
                )}

                {showTotal ? (
                    <div className="booking-bottom-total" aria-label="Total de la reserva">
                        <span className="booking-bottom-total-label">TOTAL</span>
                        <span className="booking-bottom-total-value">
                            {currencyFormatter.format(totalAmount)}
                        </span>
                    </div>
                ) : showSummary ? (
                    <button
                        type="button"
                        className={`booking-bottom-btn booking-bottom-btn--summary ${isSummaryOpen ? 'active' : ''}`}
                        onClick={() => onSummaryOpenChange(!isSummaryOpen)}
                        aria-expanded={isSummaryOpen}
                        aria-controls="booking-selection-panel"
                    >
                        <span className="booking-bottom-btn-icon booking-bottom-btn-icon--summary" aria-hidden="true">
                            ☰
                        </span>
                        <span className="booking-bottom-btn-label">Tu reserva</span>
                    </button>
                ) : (
                    <span className="booking-bottom-spacer" aria-hidden="true" />
                )}

                {showPaymentButton ? (
                    <button
                        type="button"
                        className="booking-bottom-btn booking-bottom-btn--next booking-bottom-btn--pay"
                        onClick={onPay}
                        aria-describedby={!isPaymentReady ? 'personal-data-required-hint' : undefined}
                    >
                        <span className="booking-bottom-btn-icon booking-bottom-btn-icon--lock" aria-hidden="true">
                            <LockIcon />
                        </span>
                        <span className="booking-bottom-btn-label">{getNextButtonLabel(currentStep)}</span>
                        <span className="booking-bottom-btn-icon" aria-hidden="true">›</span>
                    </button>
                ) : showNext ? (
                    <button
                        type="button"
                        className="booking-bottom-btn booking-bottom-btn--next"
                        onClick={handleNext}
                    >
                        <span className="booking-bottom-btn-label">{getNextButtonLabel(currentStep)}</span>
                        <span className="booking-bottom-btn-icon" aria-hidden="true">
                            {currentStep === BOOKING_STEPS.SUMMARY ? '✓' : '›'}
                        </span>
                    </button>
                ) : (
                    <span className="booking-bottom-spacer" aria-hidden="true" />
                )}
            </div>
        </nav>
    );
};

export default BookingBottomBar;
